import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { outgoingCallTallyQuery } from "@/app/CallbackLeads/GraphQL/outgoingCallTallyQuery";
import { Select } from "@/components/catalyst/select";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { useState } from "react";
import { useTranslations } from "use-intl";
import colors from "tailwindcss/colors";
import dayjs from "dayjs";

const PERIODS = {
  today: 0,
  week: 6,
  month: 29,
} as const;

type Period = keyof typeof PERIODS;

/**
 * How hard each rep is working the phones, which is what call targets are set
 * against. The rate counts only the hours the team is on the phones — a
 * weekday between 8am and 4pm — so a morning reads the same as a full day.
 */
export function OutgoingCallTally() {
  const t = useTranslations("callbackLead");
  const [period, setPeriod] = useState<Period>("today");

  const { data, isPending } = useGqlQuery(outgoingCallTallyQuery, {
    from: dayjs().subtract(PERIODS[period], "day").format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
  });

  const tallies = data?.outgoingCallTally ?? [];
  const total = tallies.reduce((sum, tally) => sum + tally.calls, 0);

  const chartData = tallies.map((tally) => ({
    name: tally.user?.name ?? t("tally.unattributed"),
    rate: tally.calls_per_hour,
    calls: tally.calls,
    connected: tally.connected,
    minutes: Math.round(tally.talk_seconds / 60),
  }));

  const chartConfig = {
    rate: {
      label: t("tally.rate"),
      color: colors.blue["500"],
    },
  } satisfies ChartConfig;

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-x-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            {t("tally.title")}
          </h4>
          <p className="text-xs text-gray-500">
            {t("tally.subtitle", {
              calls: total,
              hours: tallies[0]?.working_hours ?? 0,
            })}
          </p>
        </div>
        <Select
          className="max-w-40"
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
        >
          {Object.keys(PERIODS).map((option) => (
            <option key={option} value={option}>
              {t(`tally.${option as Period}`)}
            </option>
          ))}
        </Select>
      </div>

      {chartData.length ? (
        <ChartContainer
          config={chartConfig}
          className="mt-4 aspect-auto w-full"
          style={{ height: 200 }}
        >
          <BarChart data={chartData} margin={{ left: -20, top: 16 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={0}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={40}
              allowDecimals={false}
            />
            <ChartTooltip content={<CallTallyTooltip />} />
            <Bar
              dataKey="rate"
              fill={chartConfig.rate.color}
              radius={[4, 4, 0, 0]}
              maxBarSize={64}
            >
              <LabelList
                dataKey="rate"
                position="top"
                className="fill-gray-500"
                fontSize={11}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      ) : (
        <p className="mt-3 text-sm text-gray-500">
          {isPending ? t("tally.loading") : t("tally.empty")}
        </p>
      )}
    </div>
  );
}

type CallTallyDatum = {
  name: string;
  rate: number;
  calls: number;
  connected: number;
  minutes: number;
};

function CallTallyTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: CallTallyDatum }[];
}) {
  const t = useTranslations("callbackLead");

  if (!active || !payload?.length) {
    return null;
  }

  const datum = payload[0].payload;

  return (
    <div className="border-border/50 bg-background grid min-w-44 gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium">{datum.name}</div>
      <TooltipRow label={t("tally.rate")} value={datum.rate.toFixed(1)} />
      <TooltipRow
        label={t("tally.calls")}
        value={datum.calls.toLocaleString()}
      />
      <TooltipRow
        label={t("tally.connected")}
        value={datum.connected.toLocaleString()}
      />
      <TooltipRow
        label={t("tally.talkTime")}
        value={t("tally.minutes", { minutes: datum.minutes })}
      />
    </div>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-mono font-medium tabular-nums">
        {value}
      </span>
    </div>
  );
}
